import React, { Fragment, useState, useEffect, useRef, useContext } from 'react';
import { ThemeProvider } from '@material-ui/core/styles';
import { Paper, Container, Button } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useOrderPaymentStyle } from './useOrderPaymentStyle';
import { theme } from '../../styles/theme';
import { useLocation, useHistory } from 'react-router-dom';
import ENDPOINT from '../graphql/GraphQLConstants';
import { RealexHpp } from './realexPayments';
import Loading from '../shared/Loading';
import { useApolloClient } from '@apollo/client';
import { CheckoutContext } from '../eir-checkout/EirCheckoutContainer';
import Constants from '../../Constants';
import PubSub from 'pubsub-js';

export default function EirPayment(props) {
    const classes = useOrderPaymentStyle();
    const { setCurrentUrl, nextStepUrl } = props;
    const history = useHistory();
    const location = useLocation();
    const currentUrl = location.pathname;
    const prospectUuid = localStorage.getItem('prospectUuid');
    const { cartDetails } = useContext(CheckoutContext);

    const onSubmit = () => {
        history.push(nextStepUrl);
    };

    useEffect(() => {
        setCurrentUrl(currentUrl);
        const fetchData = async () => {
            await getHPPRequest();
            payButtonRef.current.click();
            setIsLoading(false);
        };
        scrollBackToTop();
        fetchData();
    }, []);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(false);
    const payButtonRef = useRef(null);
    const graphql = useApolloClient();

    const scrollBackToTop = () => {
        let scrollStep = -window.pageYOffset / (1000 / 15);
        let scrollInterval = setInterval(function () {
            if (window.pageYOffset != 0) {
                window.scrollBy(0, scrollStep);
            } else clearInterval(scrollInterval);
        }, 15);
    };

    const handleRetry = () => {
        setRetry(false);
        // Not the best way to handle retries, this is one way to reload page without leaving the client
        // The reason to not reload the iframe is because the eventlistener is duplicated forcing multiple calls
        // https://github.com/globalpayments/rxp-js/issues/31
        history.push('/retry');
        history.goBack();
    };

    const handleCallback = async response => {
        try {
            // Check if response contains valid HPP JSON
            if (!response.startsWith('{')) {
                throw new Error(response);
            }
            const { data } = await graphql.mutate({
                mutation: ENDPOINT.VALIDATE_PAYMENT,
                variables: {
                    prospectUuid: prospectUuid,
                    HppResponse: { encoded: true, hppResponse: response },
                },
            });
            if (data) {
                // On successful payment, will clear cart
                localStorage.removeItem('prospectUuid');
                localStorage.removeItem('offerId');
                localStorage.removeItem('plan-name');
                localStorage.removeItem('handset-name');
                localStorage.removeItem('prepayHandsetId');
                try {
                    // reduce the stock if cart has handset
                    if (cartDetails.device) {
                        await graphql.mutate({
                            mutation: ENDPOINT.DECREASE_STOCK,
                            variables: {
                                catalogHandsetCode: cartDetails.device.variants[0].catalogHandsetCode,
                            },
                        });
                    }
                } catch (err) {
                    console.error(err);
                }
                var cartEmpty={'cartDetails': {'numberOfProducts':0}};                   
                PubSub.publish(Constants.eventKeys.cartSubscriberAdded, cartEmpty);
                onSubmit();
            }
        } catch (err) {
            console.error(err);
            setError(err.message);
            setRetry(true);
        }
    };

    const launchHPP = async hppRequest => {
        RealexHpp.embedded.init('pay-button', 'targetIframe', handleCallback, hppRequest);
    };

    const getHPPRequest = async () => {
        try {
            setIsLoading(true);
            if (!prospectUuid) {
                throw new Error('prospectUuid not found');
            }
            const { data } = await graphql.query({
                query: ENDPOINT.GET_HPP_REQUEST,
                variables: { prospectUuid: prospectUuid },
            });
            if (data.hppRequest) {
                // Elavon complaints when null values are sent so filtering them out
                let filteredHppRequest = Object.assign({}, data.hppRequest);
                Object.keys(filteredHppRequest).forEach(
                    key => filteredHppRequest[key] == null && delete filteredHppRequest[key]
                );
                await launchHPP(filteredHppRequest);
            }
        } catch (err) {
            console.error(err);
            setIsLoading(false);
            setError(err.message);
        }
    };

    return (
        <Fragment>
            <ThemeProvider theme={theme}>
                <form>
                    <Container>
                        <div className={classes.eirOrderPayment}>
                            <h1 id='payment-details'>Payment Details</h1>
                            <Paper className={classes.paper}>
                                <iframe id='targetIframe' frameBorder='0' />
                                {isLoading && <Loading />}
                                {error && (
                                    <Dialog
                                        open={retry}
                                        onClose={handleRetry}
                                        disableBackdropClick='true'
                                        aria-labelledby='alert-dialog-title'
                                        aria-describedby='alert-dialog-description'
                                    >
                                        <DialogTitle id='alert-dialog-title'>
                                            Payment error
                                        </DialogTitle>
                                        <DialogContent>
                                            <DialogContentText id='alert-dialog-description'>
                                                {error}
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions className={classes.eirPaymentDialog}>
                                            <Button
                                                className={classes.eirButton}
                                                onClick={handleRetry}
                                                color='primary'
                                                autoFocus
                                            >
                                                Retry
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                )}
                                <Button id='pay-button' ref={payButtonRef} />
                            </Paper>
                        </div>
                    </Container>
                </form>
            </ThemeProvider>
        </Fragment>
    );
}
